/**
 * SnapshotProcessor - Handles snapshot processing with compression and metrics
 */

import { NodeOperation } from '../types/projectData';
import { ApplicationSnapshot } from '../types/applicationState';
import { snapshotPerformanceMonitor, SnapshotMetrics } from './snapshotPerformance';
import { snapshotCompressor, CompressedSnapshot } from './snapshotCompression';
import { deepClone } from './clone';
import { log } from './logger';
import { HistorySnapshot } from './snapshotHistory';

export interface ProcessedSnapshot {
  id: string;
  timestamp: number;
  operation: NodeOperation;
  state: ApplicationSnapshot;
  changes?: HistorySnapshot['changes'];
  metrics?: SnapshotMetrics;
  compressed?: CompressedSnapshot;
}

export class SnapshotProcessor {
  /**
   * Process snapshot immediately with performance monitoring
   */
  static async processSnapshotImmediate(
    state: ApplicationSnapshot,
    operation: NodeOperation,
    changes: HistorySnapshot['changes'] | undefined,
    snapshotId: string,
    enablePerformanceMonitoring: boolean,
    enableCompression: boolean,
    previousSnapshot?: ApplicationSnapshot
  ): Promise<ProcessedSnapshot> {
    let metrics: SnapshotMetrics | undefined;
    let compressed: CompressedSnapshot | undefined;
    let clonedState: ApplicationSnapshot;

    if (enablePerformanceMonitoring) {
      // Measure performance while processing
      const { result, metrics: perfMetrics } = await snapshotPerformanceMonitor.measureSnapshotCreation(
        snapshotId,
        state,
        () => {
          // Clone the state
          clonedState = this.cloneApplicationState(state);

          // Apply compression if enabled
          if (enableCompression) {
            compressed = snapshotCompressor.compress(snapshotId, state, previousSnapshot);
          }

          return clonedState;
        }
      );

      metrics = perfMetrics;
      clonedState = result;
    } else {
      // Process without performance monitoring
      clonedState = this.cloneApplicationState(state);

      if (enableCompression) {
        compressed = snapshotCompressor.compress(snapshotId, state, previousSnapshot);
      }
    }

    return {
      id: snapshotId,
      timestamp: Date.now(),
      operation,
      state: clonedState,
      changes,
      metrics,
      compressed
    };
  }

  /**
   * Process snapshot in background (simulated with setTimeout for browser)
   */
  static processSnapshotBackground(
    state: ApplicationSnapshot,
    operation: NodeOperation,
    changes: HistorySnapshot['changes'] | undefined,
    snapshotId: string,
    enablePerformanceMonitoring: boolean,
    enableCompression: boolean,
    previousSnapshot: ApplicationSnapshot | undefined,
    onComplete: (processed: ProcessedSnapshot) => void
  ): void {
    // Process compression and metrics in background
    setTimeout(async () => {
      try {
        const processed = await this.processSnapshotImmediate(
          state,
          operation,
          changes,
          snapshotId,
          enablePerformanceMonitoring,
          enableCompression,
          previousSnapshot
        );
        onComplete(processed);
      } catch (error) {
        log.error('SnapshotProcessor', 'Error processing snapshot in background:', error);
      }
    }, 0);
  }

  /**
   * Deep clone application state to ensure immutability
   */
  static cloneApplicationState(state: ApplicationSnapshot): ApplicationSnapshot {
    return deepClone(state);
  }
}
